import { useState, type FormEvent } from "react";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BusyLabel } from "@/components/admin/feedback";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function RevokeLicenseDialog({
  open,
  onOpenChange,
  licenseId,
  activationCount,
  busy,
  onRevoke,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  licenseId: string;
  activationCount: number;
  busy: boolean;
  onRevoke: (reason: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [validation, setValidation] = useState<string | null>(null);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!reason.trim()) {
      setValidation(t("dialogs.revokeLicenseReasonRequired", "A reason is required to revoke this License."));
      return;
    }
    setValidation(null);
    onRevoke(reason.trim());
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setReason("");
      setValidation(null);
    }
    onOpenChange(nextOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="border-danger/35 sm:max-w-md">
        <form onSubmit={submit}>
          <DialogHeader>
            <div className="flex items-center gap-2 text-danger">
              <AlertTriangle className="size-5" aria-hidden="true" />
              <DialogTitle className="text-base font-semibold text-danger">
                {t("dialogs.revokeLicenseTitle", "Revoke License")}
              </DialogTitle>
            </div>
            <DialogDescription className="mt-2 text-xs leading-5 text-muted">
              {t("dialogs.revokeLicenseConfirm", "Revoke this License. New Product Key issuance and new token issuance will be blocked.")}
              <span className="mt-1 block font-mono text-ink">{licenseId} ({activationCount} linked Activations)</span>
            </DialogDescription>
          </DialogHeader>

          <p className="mt-3 text-xs leading-5 text-muted">
            {t("dialogs.revokeLicenseOfflineConsequence", "Existing signed offline tokens can continue until their token lifetime or offline window expires. Record why this License is being revoked.")}
          </p>

          <div className="mt-4 space-y-2">
            <Label htmlFor="revoke-dialog-reason" className="text-xs">
              {t("dialogs.reason", "Reason")} <span className="text-danger">*</span>
            </Label>
            <Input
              id="revoke-dialog-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              aria-invalid={Boolean(validation)}
              aria-describedby={validation ? "revoke-license-reason-error" : undefined}
              placeholder={t("dialogs.revokeLicenseReasonPlaceholder", "e.g. customer contract termination")}
              autoFocus
              disabled={busy}
            />
            {validation && (
              <p id="revoke-license-reason-error" className="text-xs text-danger" role="alert">
                {validation}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => handleOpenChange(false)}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              variant="danger"
              size="sm"
              disabled={!reason.trim() || busy}
            >
              {busy ? <BusyLabel>{t("dialogs.revoking", "Revoking")}</BusyLabel> : t("dialogs.confirmRevoke", "Revoke")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
